import React, { useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import PageLayout from '@/common-components/page-layout/page-layout';
import { useFindSessionsByNodeEventQuery } from '@common/reducers/backend-api/backend-api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EventType } from '@common/models/node-event';
import { Button } from '@/components/ui/button';

const DEFAULT_LIMIT = 20;

export const SessionListPage: React.FC = () => {
  const { t } = useTranslation(namespace);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract filter parameters from URL
  const eventType = searchParams.get('eventType') as EventType | null;
  const processType = searchParams.get('processType') || null;
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || `${DEFAULT_LIMIT}`, 10);
  const skip = (currentPage - 1) * limit;

  const queryParams = useMemo(() => ({
    ...(eventType && { eventType }),
    ...(processType && { processType }),
    limit,
    skip,
  }), [eventType, processType, limit, skip]);

  // Fetch sessions based on filters
  const { data: sessionsData, isLoading, isError, error } = useFindSessionsByNodeEventQuery(queryParams, {
    skip: !eventType && !processType, // Skip query if no filters are present
  });

  // Calculate pagination details
  const totalItems = sessionsData?.total ?? 0;
  const totalPages = Math.ceil(totalItems / limit);
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Handle page navigation
  const handlePageChange = (newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPage.toString());
    navigate(`?${newSearchParams.toString()}`);
  };

  const pageTitle = useMemo(() => {
    if (eventType) return t('sessionList.titleWithEventType', { eventType });
    if (processType) return t('sessionList.titleWithProcessType', { processType });
    return t('sessionList.title');
  }, [eventType, processType, t]);

  const pageDescription = useMemo(() => {
    if (eventType) return t('sessionList.descriptionWithEventType', { eventType });
    if (processType) return t('sessionList.descriptionWithProcessType', { processType });
    return t('sessionList.description');
  }, [eventType, processType, t]);

  return (
    <PageLayout title={pageTitle}>
      <p className="text-slate-500 mb-4">{pageDescription}</p>
      
      {isLoading && (
        <div className="h-60 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertTitle>{t('sessionList.errorLoadingSessions')}</AlertTitle>
          <AlertDescription>
            {(error as any)?.data?.message || (error as any)?.error || t('sessionList.unknownError')}
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && sessionsData && (
        <> 
          {sessionsData.data.length === 0 ? (
            <div className="h-60 flex items-center justify-center text-slate-500">
              {t('sessionList.noSessionsFound')}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('sessionList.tableHeader.sessionId')}</TableHead>
                    <TableHead>{t('sessionList.tableHeader.botId')}</TableHead>
                    <TableHead>{t('sessionList.tableHeader.status')}</TableHead>
                    <TableHead>{t('sessionList.tableHeader.createdAt')}</TableHead>
                    <TableHead className="text-right">{t('sessionList.tableHeader.lastUpdated')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessionsData.data.map((session) => (
                    <TableRow 
                      key={session._id}
                      onClick={() => navigate(`/bot-detail-view/${session.botId}/session/${session._id}`)}
                      className="cursor-pointer hover:bg-slate-100/50"
                    >
                      <TableCell className="font-mono text-xs">{session._id}</TableCell>
                      <TableCell className="font-mono text-xs">{session.botId}</TableCell>
                      <TableCell>{session.status}</TableCell>
                      <TableCell>{new Date(session.createdAt).toLocaleString()}</TableCell>
                      <TableCell className="text-right">{new Date(session.updatedAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-end space-x-2 py-4">
                  <div className="flex-1 text-sm text-slate-500">
                    {t('sessionList.pagination', { currentPage, totalPages, totalItems })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!canGoPrevious}
                  >
                    {t('common.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!canGoNext}
                  >
                    {t('common.next')}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {!isLoading && !isError && !sessionsData && !eventType && !processType && (
         <Alert variant="destructive">
          <AlertTitle>{t('sessionList.missingFilter')}</AlertTitle>
          <AlertDescription>
            {t('sessionList.missingFilterDescription')}
          </AlertDescription>
        </Alert>
      )}

    </PageLayout>
  );
}; 